<template>
  <modal-vue
    :modal-active="modalActive"
    has-header-border
    has-footer-border
    max-width="max-w-[500px]"
    @close="handleClose"
  >
    <template #header>
      <h2 class="text-xl font-bold text-textPrimary text-left">Ajustar foto de perfil</h2>
    </template>

    <div class="flex flex-col gap-4">
      <Cropper
        v-if="imageSrc"
        ref="cropperRef"
        class="h-72 w-full rounded-lg bg-gray-900"
        :src="imageSrc"
        :stencil-component="CircleStencil"
        :stencil-props="{ aspectRatio: 1 }"
        :canvas="{ maxWidth: outputSize, maxHeight: outputSize, fillColor: '#ffffff' }"
        image-restriction="stencil"
        data-testid="avatar-crop-cropper"
      />

      <div class="flex items-center gap-3 px-1">
        <v-icon size="18" class="text-gray-500">{{ mdiMagnifyMinusOutline }}</v-icon>
        <v-slider
          :model-value="zoomLevel"
          :min="1"
          :max="3"
          :step="0.01"
          hide-details
          color="primary"
          track-color="grey-lighten-2"
          data-testid="avatar-crop-zoom-slider"
          @update:model-value="onZoomSliderChange"
        />
        <v-icon size="18" class="text-gray-500">{{ mdiMagnifyPlusOutline }}</v-icon>
      </div>

      <p class="text-sm text-gray-500 text-center">
        Arraste para posicionar e use o zoom para ajustar a foto.
      </p>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full mt-2">
        <outlined-button
          data-testid="avatar-crop-cancel-button"
          text="Cancelar"
          width="120px"
          class="!border-primary30 !text-primary30 !font-bold"
          @click="handleClose"
        />

        <filled-button
          data-testid="avatar-crop-save-button"
          text="Salvar"
          width="120px"
          :loading="isExporting"
          class="!bg-primary !text-white !font-bold hover:!bg-primary40"
          @click="handleSave"
        />
      </div>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import { useToast } from 'vue-toastification'
import { mdiMagnifyMinusOutline, mdiMagnifyPlusOutline } from '@mdi/js'
import { Cropper, CircleStencil } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'

const props = withDefaults(
  defineProps<{
    modalActive: boolean
    file: File | null
    outputSize?: number
  }>(),
  { outputSize: 512 }
)

const emit = defineEmits<{
  close: []
  cropped: [file: File]
}>()

const toast = useToast()

const EXPORT_ERROR_MESSAGE = 'Não foi possível processar a imagem. Tente novamente.'

const cropperRef = ref<InstanceType<typeof Cropper> | null>(null)
const imageSrc = ref<string | null>(null)
const isExporting = ref(false)
const zoomLevel = ref(1)

const revokeCurrentUrl = () => {
  if (imageSrc.value) {
    URL.revokeObjectURL(imageSrc.value)
    imageSrc.value = null
  }
}

watch(
  () => props.file,
  (file) => {
    revokeCurrentUrl()
    zoomLevel.value = 1
    if (file) imageSrc.value = URL.createObjectURL(file)
  },
  { immediate: true }
)

onBeforeUnmount(revokeCurrentUrl)

const onZoomSliderChange = (value: number) => {
  // zoom() da lib e relativo: com bind one-way no slider, zoomLevel ainda
  // guarda o valor anterior neste momento
  if (zoomLevel.value > 0) {
    cropperRef.value?.zoom(value / zoomLevel.value)
  }
  zoomLevel.value = value
}

const handleClose = () => {
  // Fechar durante o export dispararia o upload de um modal ja fechado
  if (isExporting.value) return
  emit('close')
}

const canvasToJpegBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92))

const handleSave = async () => {
  // O canvas ja sai limitado a outputSize e com fundo branco via prop :canvas
  const canvas = cropperRef.value?.getResult().canvas
  if (!canvas) {
    toast.error(EXPORT_ERROR_MESSAGE)
    return
  }

  isExporting.value = true
  try {
    const blob = await canvasToJpegBlob(canvas)
    if (!blob) {
      toast.error(EXPORT_ERROR_MESSAGE)
      return
    }
    emit('cropped', new File([blob], 'avatar.jpg', { type: 'image/jpeg' }))
  } finally {
    isExporting.value = false
  }
}
</script>
